import { Fragment } from 'react';
import { useConfig, Store } from '../../config';
import { useScript } from '../../hooks/useScript';
import { useTranslation } from '../../i18n/i18n';
import { companies, companyFor, footerAddress, footerIdentifier } from '../../seo/organizations';
import './Footer.css';
import { FooterLists, FooterServiceColumn } from './FooterLists';

export const Footer = (): JSX.Element => {
	const config = useConfig();
	const { logo, store, storeName } = config;
	const footerTrans = useTranslation().footer;

	return (
		<footer className="footer">
			<div>
				<div className="logo">
					<img
						src={`/images/${logo}`}
						alt={storeName}
						width="129"
						height="67"
						fetchpriority="high"
						loading="eager"
					/>
					<FooterServiceColumn />
				</div>
				<div className="customer-service only-mobile">
					<p className="heading">{footerTrans.customerService}</p>
					<ul>
						<li>
							<a href={`mailto:${config.customerServiceEmail}`}>{config.customerServiceEmail}</a>
						</li>
						<li>
							<a href={`tel:${config.customerServicePhoneNumber}`}>{config.customerServicePhoneNumber}</a>
						</li>
						{/*
						 * The hours come from the translation rather than from here: how a market
						 * writes a time range is its own convention, and German asks for 8:30-17:00
						 * with an en dash where the others use dots and a hyphen.
						 */}
						<li>
							{footerTrans.openDays} {footerTrans.openHoursShort}
						</li>
					</ul>
				</div>
				<FooterLists />
				<OrgInfo store={store} />
				<ServiceLogos store={store} />
			</div>
			<span className="version-label">version: {config.version?.substring(0, 7)}</span>
		</footer>
	);
};

const ServiceLogos = ({ store }: { store: Store }) => {
	const config = useConfig();
	useScript(config.ehandelsCertifieringUrl);

	const masterCardLogo = (
		<img
			src="/images/mastercard-logo.png"
			alt="mastercard logo"
			className="mastercard-logo"
			loading="lazy"
			width="50"
			height="30"
		/>
	);
	const klarnaLogo = (
		<img
			src="/images/klarna-logo.png"
			alt="klarna logo"
			className="klarna-logo"
			loading="lazy"
			width="91"
			height="20"
		/>
	);

	const visaLogo = (
		<img src="/images/visa-logo.png" alt="visa logo" className="visa-logo" loading="lazy" width="56" height="32" />
	);

	const kgseEcertLogo = (
		<span className="e-cert-logo">
			<a id="celink4" href="http://www.ehandelscertifiering.se/">
				certifierad ehandel
			</a>
		</span>
	);

	const swishLogo = (
		<img
			src="/images/swish-logo.png"
			alt="swish logo"
			className="swish-logo"
			loading="lazy"
			width="96"
			height="30"
		/>
	);

	const postnordLogo = (
		<img
			src="/images/postnord-logo.png"
			alt="postnord logo"
			width="90"
			height="20"
			className="bring-postnord-logo"
			loading="lazy"
		/>
	);

	const kgnoEcertLogo = (
		<span className="e-cert-logo">
			<a id="celink509" href="http://www.ehandelssertifisering.no/">
				sertifisert ehandel
			</a>
		</span>
	);

	const bringLogo = (
		<img
			src="/images/bring-logo.svg"
			alt="bring logo"
			className="bring-postnord-logo"
			loading="lazy"
			width="90"
			height="34"
		/>
	);

	const kgdkEcertLogo = (
		<span className="e-cert-logo">
			<a id="celink747" href="http://www.ehandelscertificering.dk/">
				certificeret ehandel
			</a>
		</span>
	);

	const eshopLogo = (
		<div className="e-shop-cert-wrapper">
			<img className="e-shop-cert-logo" src="/images/dankort-logo.svg" width="144" height="54" />
		</div>
	);

	const bringLogo2 = (
		<img
			src="/images/bring-logo.svg"
			alt="bring logo"
			className="bring-postnord-logo"
			loading="lazy"
			width="90"
			height="34"
		/>
	);

	const mobilepayLogo = (
		<img
			src="/images/mobilepay-logo.svg"
			alt="mobilepay logo"
			className="swish-logo mobilepay"
			width="215"
			height="39"
		/>
	);

	const glsLogo = (
		<img src="/images/gls-logo.png" alt="gls logo" className="gls-logo" loading="lazy" width="100" height="30" />
	);

	const paypalLogo = (
		<img src="/images/paypal-logo.svg" alt="paypal logo" className="paypal-logo" loading="lazy" height="45" />
	);

	const dpdLogo = <img src="/images/dpd-logo.png" alt="dpd logo" className="dpd-logo" loading="lazy" height="30" />;

	const logos = {
		kgse: [
			<Fragment key="mastercard">{masterCardLogo}</Fragment>,
			<Fragment key="klarna">{klarnaLogo}</Fragment>,
			<Fragment key="visa">{visaLogo}</Fragment>,
			<Fragment key="ecert">{kgseEcertLogo}</Fragment>,
			<Fragment key="swish">{swishLogo}</Fragment>,
			<Fragment key="postnord">{postnordLogo}</Fragment>,
		],
		kgno: [
			<Fragment key="mastercard">{masterCardLogo}</Fragment>,
			<Fragment key="klarna">{klarnaLogo}</Fragment>,
			<Fragment key="visa">{visaLogo}</Fragment>,
			<Fragment key="ecert">{kgnoEcertLogo}</Fragment>,
			<Fragment key="bring">{bringLogo}</Fragment>,
		],
		kgdk: [
			<Fragment key="klarna">{klarnaLogo}</Fragment>,
			<Fragment key="visa">{visaLogo}</Fragment>,
			<Fragment key="ecert">{kgdkEcertLogo}</Fragment>,
			<Fragment key="eshop">{eshopLogo}</Fragment>,
			<Fragment key="bring">{bringLogo2}</Fragment>,
			<Fragment key="mobilepay">{mobilepayLogo}</Fragment>,
		],
		kgnl: [
			<Fragment key="mastercard">{masterCardLogo}</Fragment>,
			<Fragment key="klarna">{klarnaLogo}</Fragment>,
			<Fragment key="visa">{visaLogo}</Fragment>,
			<Fragment key="gls">{glsLogo}</Fragment>,
			<Fragment key="paypal">{paypalLogo}</Fragment>,
		],
		kgde: [
			<Fragment key="mastercard">{masterCardLogo}</Fragment>,
			<Fragment key="klarna">{klarnaLogo}</Fragment>,
			<Fragment key="visa">{visaLogo}</Fragment>,
			<Fragment key="dpd">{dpdLogo}</Fragment>,
			<Fragment key="paypal">{paypalLogo}</Fragment>,
		],
		kgat: [],
		kggb: [],
	};

	return <div className="service-logos">{logos[store]}</div>;
};

const OrgInfo: React.FC<{ store: Store }> = ({ store }): JSX.Element => {
	// the company behind each store lives in src/seo, where the home page's structured data reads
	// it too, so the footer and the schema.org graph cannot drift apart
	const company = companyFor(store) ?? companies.kontorsgiganten;
	let orgInfoText: JSX.Element;
	switch (store) {
		case 'kgno':
			orgInfoText = (
				<>
					<p>
						{company.legalName} org.nummer {footerIdentifier(company)}
					</p>
					<p>{footerAddress(company)}</p>
					<p>(Ikke besøksadresse)</p>
					<p>© Ondio 2002-2026</p>
				</>
			);
			break;
		case 'kgdk':
			orgInfoText = (
				<>
					<p>
						{company.legalName}, cvr: {footerIdentifier(company)}
					</p>
					<p>{footerAddress(company)}</p>
					<p>(ej for besøgende)</p>
					<p>© Ondio 2002-2026</p>
				</>
			);
			break;
		case 'kgat':
			// Austria is not live and has no verified company behind it yet
			orgInfoText = (
				<>
					<p>Ondio GmbH, FN: 123456789</p>
					<p>Hauptstraße 1, 1010 Wien</p>
					<p>(Keine Besuchsadresse)</p>
					<p>© Ondio 2002-2026</p>
				</>
			);
			break;
		case 'kgde':
			orgInfoText = (
				<>
					<p>
						{company.legalName}, HRB: {footerIdentifier(company)}
					</p>
					<p>{footerAddress(company)}</p>
					<p>(Keine Besuchsadresse)</p>
					<p>© Ondio 2026</p>
				</>
			);
			break;
		case 'kggb':
			// Britain is not live and has no verified company behind it yet
			orgInfoText = (
				<>
					<p>Kontorsgiganten UK Ltd, Company No: 12345678</p>
					<p>1 Main Street, London, EC1A 1AA</p>
					<p>(Not a visiting address)</p>
					<p>© Kontorsgiganten 2002-2026</p>
				</>
			);
			break;
		case 'kgnl':
			orgInfoText = (
				<>
					<p>
						{company.legalName}, KvK: {footerIdentifier(company)}
					</p>
					<p>{footerAddress(company)}</p>
					<p>(Geen bezoekadres)</p>
					<p>© Ondio 2026</p>
				</>
			);
			break;
		case 'kgse':
		default:
			orgInfoText = (
				<>
					<p>
						{company.legalName} org.nr. {footerIdentifier(company)}
					</p>
					<p>{footerAddress(company)}</p>
					<p>(Obs. Ej besöks eller returadress)</p>
					<p>© Kontorsgiganten 2002-2026</p>
				</>
			);
			break;
	}
	return <div className="org-info">{orgInfoText}</div>;
};
